import React, { useState, useContext } from "react";
import { Board } from "@caldwell619/react-kanban";
import parse from "html-react-parser";
import { v4 as uuidv4 } from "uuid";
import { ProcessQueue, ProcessQueueTask } from "../types";
import { useParams } from 'react-router';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

import { loginRequest, getParam, Api, useSessionId, useAuthProvider, useDataProvider, DataContext, callApi, Loader } from '@cloudpremise/portal_core';
import DashboardLayout from "@cloudpremise/portal_core/src/themes/mui/examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "@cloudpremise/portal_core/src/themes/mui/examples/Navbars/DashboardNavbar";
import MDTypography from "@cloudpremise/portal_core/src/themes/mui/components/MDTypography";
import MDInput from "@cloudpremise/portal_core/src/themes/mui/components/MDInput";
import MDBox from "@cloudpremise/portal_core/src/themes/mui/components/MDBox";
import MDButton from "@cloudpremise/portal_core/src/themes/mui/components/MDButton";
import { useMaterialUIController } from "@cloudpremise/portal_core/src/themes/mui/context";
import Icon from "@mui/material/Icon";
import { Theme } from "@mui/material/styles";
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';

const Project = (props) => {
    const dataProvider = useContext(DataContext);
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const params = useParams();

    const defaultState = {
        loading: false,
        loadingMore: false,
        response: null,
        RF_LIMIT: 20,
        RF_OFFSET: null,
        itemModal: false,
        item: null,
        newCardForm: false,
        newTask: '',
        board: null,
        waiting: [],
        working: [],
        complete: [],
        exception: []
    };

    const [state, setState] = React.useReducer((state: any, newState: Partial<any>) => ({
        ...state,
        ...newState
    }), defaultState);

    const queue: ProcessQueue = {
        sObjectTypeName: "ProcessQueue__c",
        url: "/v1/bpm/queue/"+params.id
    }
    const [loading, data, adapterState, setAdapterState] = useDataProvider(queue, {
        "RF_LIMIT": state.RF_LIMIT,
        "RF_OFFSET": state.RF_OFFSET,
        "provider": "apexadapter"
    }, false, (response) => {
        let result = null;
        if(response && typeof(response.result) === "object"){
            result = response.result;
            if(Array.isArray(result)){
                result = result[0];
            }
        }
        let board = null;
        let columns = {};
        if(result && Array.isArray(result.processTasks)){
            const processTaskStatus = result.processTaskStatus;
            const processTaskStatusArray = processTaskStatus.split(",");            
            processTaskStatusArray.map((status) => {
                status = status.trim();
                columns[prepareId(status)] = {
                    title: status,
                    cards: []
                };
            });
            result.processTasks.map((task) => {
                const taskDetail = {
                    id: task.processTaskId,
                    template: task.description,
                };
                if(processTaskStatus.indexOf(task.status) !== -1){
                    const columnId = prepareId(task.status);
                    columns[columnId]['title'] = task.status;
                    if(!Array.isArray(columns[columnId]['cards'])){
                        columns[columnId]['cards'] = [];
                    }
                    columns[columnId]['cards'].push(taskDetail);
                }
            });
            board = getBoard(columns);
        }

        setState({
            response: result,
            board: board,
            columns: columns
        });
    });

    function prepareId(text){
        return text.replace(" ", "-").toLowerCase();
    }

    function getBoard(columns){
        const board = {
            columns: []
        };
        for(let id in columns){
            if(!columns.hasOwnProperty(id)){
                continue;
            }
            const column = columns[id];
            board['columns'].push({
                id: id,
                title: column.title,
                cards: column.cards
            });
        }
        return board;
    }

    function handleChange(e, name){
        setState({
            [name]: e.target.value,
        });
    }

    const openNewCardForm = (event: HTMLButtonElement | any, id: string | number) => {
        setState({
            newCardForm: id
        });
    }
    const closeNewCardForm = () => {
        setState({
            newCardForm: false
        });
    }

    const onAddNewCardSuccess = (id, title) => {
        const { newTask, response } = state;
        let columns = state.columns;        
        const currentTasks = columns[id].cards;
        const taskId = uuidv4();
        const newTasks = [{
            id: taskId,
            template: newTask
        }].concat(currentTasks);
        columns[id].cards = newTasks;
        
        let board = getBoard(columns);
        const requestData = {
            processQueueId: params.id,
            description: newTask,
            status: title,
            assignedToId: response.defaultAssigneeId
        };
        const queue: ProcessQueueTask = {
            sObjectTypeName: 'ProcessTask__c',
            url: '/v1/bpm/task',
            method: 'POST',
            headers: {},
            data: requestData
        };

        callApi(dataProvider, queue).then((response: any) => {
            const processTaskId = response.result.processTaskId;
            let columns = state.columns;        
            const cards = columns[id].cards;
            cards.map((card, key) => {
                if(card.id === taskId){
                    cards[key].id = processTaskId;
                }
            });
            columns[id].cards = cards;
            board = getBoard(columns);
            setState({
                board: board,
                columns: columns
            });
        }).catch((err) => {
            let errorMessage = null;
            if(typeof(err) === "object" && err.hasOwnProperty("error")){
                errorMessage = err.error;
            }else if(typeof(err) === "object" && err.hasOwnProperty("message")){
                errorMessage = err.message;
            }
        });

        setState({
            board: board,
            columns: columns,
            newCardForm: false,
            newTask: ''
        });
    }

    function onCardDragEnd(card, source, destination){
        let columns = state.columns;
        const oldColumns = JSON.parse(JSON.stringify(columns)); // Hack to clone object so that we can revert if api fails
        let sourceColumn = columns[source.fromColumnId];
        let destinationColumn = columns[destination.toColumnId];
        let sourceCards = [];
        let destinationCards = [];
        sourceColumn.cards.map((item) => {
            if(item.id !== card.id){
                sourceCards.push(item);
            }
        });
        sourceColumn.cards = sourceCards;
        columns[source.fromColumnId] = sourceColumn;

        let cardPushed = false;
        if(destinationColumn.cards.length > 0){
            destinationColumn.cards.map((item, key) => {
                if(key === destination.toPosition){
                    destinationCards.push(card);
                    cardPushed = true;
                }
                destinationCards.push(item);
            });
        }
        if(!cardPushed){
            destinationCards.push(card);
        }
        
        destinationColumn.cards = destinationCards;
        columns[destination.toColumnId] = destinationColumn;
        let board = getBoard(columns);
        const requestData = {
            status: destinationColumn.title,
        };

        const queue: ProcessQueueTask = {
            sObjectTypeName: 'ProcessTask__c',
            url: '/v1/bpm/task/'+card.id,
            method: 'PATCH',
            headers: {},
            data: requestData
        };

        callApi(dataProvider, queue).then((response: any) => {
            
        }).catch((err) => {
            let errorMessage = null;
            if(typeof(err) === "object" && err.hasOwnProperty("error")){
                errorMessage = err.error;
            }else if(typeof(err) === "object" && err.hasOwnProperty("message")){
                errorMessage = err.message;
            }
            board = getBoard(oldColumns);
            setState({
                board: board,
                columns: oldColumns
            });
        });

        setState({
            board: board,
            columns: columns
        });
    }

    function onColumnDragEnd(column, source, destination){
        let columns = state.columns;
        let newColumns = {};
        let index = 0;
        let processTaskStatus = [];
        let columnPushed = false;
        for(let id in columns){
            if(destination.toPosition === index){
                newColumns[column.id] = column;
                processTaskStatus.push(column.title);
                columnPushed = true;
            }
            if(column.id !== id){
                newColumns[id] = columns[id];
                processTaskStatus.push(columns[id].title);
                index++;
            }
        }
        if(!columnPushed){
            newColumns[column.id] = column;
            processTaskStatus.push(column.title);
        }
        let board = getBoard(newColumns);
        const requestData = {
            processQueueId: params.id,
            processTaskStatus: processTaskStatus.join(", ")
        };

        const queue: ProcessQueue = {
            sObjectTypeName: 'ProcessQueue__c',
            url: '/v1/bpm/queue/'+params.id,
            method: 'PATCH',
            headers: {},
            data: requestData
        };

        callApi(dataProvider, queue).then((response: any) => {
            
        }).catch((err) => {
            let errorMessage = null;
            if(typeof(err) === "object" && err.hasOwnProperty("error")){
                errorMessage = err.error;
            }else if(typeof(err) === "object" && err.hasOwnProperty("message")){
                errorMessage = err.message;
            }
            setState({
                errorMessage: errorMessage
            });
        });

        setState({
            board: board,
            columns: newColumns,
        });
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={3}>
                <MDBox
                    position="relative"
                    sx={({
                        palette: { light, background },
                        functions: { pxToRem },
                        borders: { borderRadius },
                    }: Theme | any) => ({
                        "& .react-kanban-column": {
                            backgroundColor: darkMode ? background.card : light.main,
                            width: pxToRem(450),
                            margin: `0 ${pxToRem(10)}`,
                            padding: pxToRem(20),
                            borderRadius: borderRadius.lg,
                        },
                    })}
                >
                    {
                        loading || state.response === null ?
                            <Loader p={2} color="info" />
                        :
                            state.board === null ?
                                state.errorMessage !== null ?
                                    <Stack p={2}>
                                        <Alert severity="error">{state.errorMessage}</Alert>
                                    </Stack>
                                :
                                    <Stack p={2}>
                                        <Alert severity="warning">Board not found.</Alert>
                                    </Stack>
                            :
                            <Board
                                allowAddCard={false}
                                allowAddColumn
                                renderColumnHeader={({ id, title }: any) => (
                                    <>
                                        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                            <MDTypography variant="h6">{title}</MDTypography>
                                            <MDButton size="small" iconOnly onClick={(event) => openNewCardForm(event, id)}>
                                                <Icon
                                                sx={{
                                                    fontWeight: "bold",
                                                    color: ({ palette: { dark } }) => dark.main,
                                                }}
                                                >
                                                add
                                                </Icon>
                                            </MDButton>
                                        </MDBox>
                                        {state.newCardForm === id ? (
                                            <MDBox my={2.5}>
                                                <MDInput
                                                value={state.newTask}
                                                rows="4"
                                                onChange={(e) => handleChange(e, "newTask")}
                                                multiline
                                                fullWidth
                                                />
                                                <MDBox display="flex" mt={2}>
                                                    <MDButton
                                                        variant="gradient"
                                                        color="success"
                                                        size="small"
                                                        onClick={() => {
                                                            onAddNewCardSuccess(id, title);
                                                        }}
                                                    >
                                                        add
                                                    </MDButton>
                                                    <MDBox ml={1}>
                                                        <MDButton
                                                        variant="gradient"
                                                        color="light"
                                                        size="small"
                                                        onClick={closeNewCardForm}
                                                        >
                                                        cancel
                                                        </MDButton>
                                                    </MDBox>
                                                </MDBox>
                                            </MDBox>
                                        ) : null}
                                    </>
                                )}
                                renderCard={({ id, template }: any, { dragging }: any) => (
                                    <MDBox
                                        key={id}
                                        dragging={dragging.toString() || undefined}
                                        display="block"
                                        width="calc(450px - 40px)"
                                        bgColor={darkMode ? "transparent" : "white"}
                                        color="text"
                                        borderRadius="xl"
                                        mt={2.5}
                                        py={1.875}
                                        px={1.875}
                                        lineHeight={1.5}
                                        sx={{
                                        border: ({ borders: { borderWidth }, palette: { white } }: any) =>
                                            darkMode ? `${borderWidth[1]} solid ${white.main}` : 0,
                                        fontSize: ({ typography: { size } }: any) => size.md,
                                        }}
                                    >
                                        {typeof template === "string" ? parse(template) : template}
                                    </MDBox>
                                )}
                                onCardDragEnd={(card, source, destination) => onCardDragEnd(card, source, destination)}
                                onColumnDragEnd={(column, source, destination) => onColumnDragEnd(column, source, destination)}
                            >
                                {state.board}
                            </Board>
                    }
                    
                </MDBox>
            </MDBox>
        </DashboardLayout>
    );
};

export default Project;